import { useState, useEffect } from 'react'

export interface Company {
  id: string
  name: string
  description: string | null
  website: string | null
  logoUrl: string | null
  createdAt: string
  updatedAt: string
  // Extended fields from Epic 18 Story 2
  employeeCount: number | null
  revenueRange: string | null
  businessBranches: string | null
  aiPainPoints: string | null
  aiAdoptionStage: string | null
  isOnboarded: boolean | null
  isDemo: boolean | null
  _count?: {
    users: number
  }
}

export interface CompanyResponse {
  company: Company
}

export interface UseCompanyResult {
  company: Company | null
  loading: boolean
  error: string | null
  refetchCompany: () => void
}

export function useCompany(companyId: string): UseCompanyResult {
  const [company, setCompany] = useState<Company | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchCompany = async () => {
    if (!companyId) {
      setCompany(null)
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      // Get auth token
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch(`/api/admin/companies/${companyId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        throw new Error(errorData.error || `Failed to fetch company (${response.status})`)
      }

      const data: CompanyResponse = await response.json()
      setCompany(data.company)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch company'
      setError(errorMessage)
      console.error('Error fetching company:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCompany()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId])

  return {
    company,
    loading,
    error,
    refetchCompany: fetchCompany
  }
}
